/**
 * MyTeamsPage (AC-890, AC-898)
 *
 * Lists all teams the current user belongs to, with the ability
 * to create new teams, respond to pending invitations, and
 * discover/search for teams to join (AC-898).
 *
 * Route: /teams
 * Parent: Layout via Outlet
 * User Access: Sidebar navigation item "My Teams"
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Crown, Search, X, Compass } from 'lucide-react';
import { useTeamStore } from '../store/teamStore';
import { useAuthStore } from '../store';
import { TeamResponse } from '../types';
import CreateTeamDialog from '../components/teams/CreateTeamDialog';
import PendingInvitationsBanner from '../components/teams/PendingInvitationsBanner';

type TabKey = 'my-teams' | 'discover';

const PAGE_SIZE = 20;

const MyTeamsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    teams,
    pendingInvitations,
    loading,
    loadMyTeams,
    loadMyInvitations,
    discoverTeams,
    discoverTotalCount,
    discoverLoading,
    loadDiscoverTeams,
  } = useTeamStore();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('my-teams');

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Discover pagination
  const [discoverSkip, setDiscoverSkip] = useState(0);

  useEffect(() => {
    loadMyTeams();
    loadMyInvitations();
  }, [loadMyTeams, loadMyInvitations]);

  // Load discover teams when tab changes or search/pagination changes
  useEffect(() => {
    if (activeTab === 'discover') {
      loadDiscoverTeams(searchQuery || undefined, discoverSkip, PAGE_SIZE);
    }
  }, [activeTab, searchQuery, discoverSkip, loadDiscoverTeams]);

  // Debounce search input (300ms, matching ExplorePage pattern)
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setDiscoverSkip(0);
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchInput]);

  const handleTeamClick = (teamId: number) => {
    navigate(`/teams/${teamId}`);
  };

  const handleTeamKeyDown = (e: React.KeyboardEvent, teamId: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/teams/${teamId}`);
    }
  };

  const isOwner = (team: TeamResponse): boolean => {
    if (!user) return false;
    return team.owner_id === Number(user.id);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setDiscoverSkip(0);
  };

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    if (tab === 'my-teams') {
      // Clear search when switching back to My Teams
      handleClearSearch();
    }
  };

  const handleNextPage = () => {
    setDiscoverSkip((prev) => prev + PAGE_SIZE);
  };

  const handlePrevPage = () => {
    setDiscoverSkip((prev) => Math.max(0, prev - PAGE_SIZE));
  };

  const isFirstPage = discoverSkip === 0;
  const isLastPage = discoverSkip + PAGE_SIZE >= discoverTotalCount;

  // Filter my teams by local search when on the My Teams tab
  const filteredMyTeams = searchInput && activeTab === 'my-teams'
    ? teams.filter(
        (team) =>
          team.name.toLowerCase().includes(searchInput.toLowerCase()) ||
          (team.description && team.description.toLowerCase().includes(searchInput.toLowerCase()))
      )
    : teams;

  const renderTeamCard = (team: TeamResponse, showOwnerBadge: boolean) => (
    <div
      key={team.team_id}
      role="button"
      tabIndex={0}
      onClick={() => handleTeamClick(team.team_id)}
      onKeyDown={(e) => handleTeamKeyDown(e, team.team_id)}
      className="cursor-pointer bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all"
    >
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
          {team.name}
        </h3>
        {showOwnerBadge && isOwner(team) && (
          <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
            <Crown className="w-3 h-3" aria-hidden="true" />
            Owner
          </span>
        )}
      </div>

      {team.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2 overflow-hidden">
          {team.description}
        </p>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1">
          <Users className="w-3.5 h-3.5" aria-hidden="true" />
          {team.member_count} members
        </span>
        {!showOwnerBadge && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            by {team.owner_username}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Teams
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
            <input
              type="text"
              placeholder={activeTab === 'my-teams' ? 'Search my teams...' : 'Search teams to join...'}
              aria-label={activeTab === 'my-teams' ? 'Search my teams' : 'Search teams to discover'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 w-48 sm:w-64"
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            Create Team
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'my-teams'}
          aria-controls="my-teams-panel"
          onClick={() => handleTabChange('my-teams')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'my-teams'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            <Users className="w-4 h-4" aria-hidden="true" />
            My Teams
          </span>
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'discover'}
          aria-controls="discover-panel"
          onClick={() => handleTabChange('discover')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'discover'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            <Compass className="w-4 h-4" aria-hidden="true" />
            Discover Teams
          </span>
        </button>
      </div>

      {/* Pending Invitations Banner - only show on My Teams tab */}
      {activeTab === 'my-teams' && (
        <PendingInvitationsBanner invitations={pendingInvitations} />
      )}

      {/* My Teams Panel */}
      {activeTab === 'my-teams' && (
        <div id="my-teams-panel" role="tabpanel">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500 dark:text-gray-400">Loading teams...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredMyTeams.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" aria-hidden="true" />
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {searchInput
                  ? `No teams found matching "${searchInput}".`
                  : "You haven't joined any teams yet."}
              </p>
              {!searchInput && (
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateDialog(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                  >
                    <Plus className="w-4 h-4" aria-hidden="true" />
                    Create Team
                  </button>
                  <button
                    onClick={() => handleTabChange('discover')}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-300 dark:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md transition-colors"
                  >
                    <Compass className="w-4 h-4" aria-hidden="true" />
                    Discover Teams
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Team Grid */}
          {!loading && filteredMyTeams.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMyTeams.map((team) => renderTeamCard(team, true))}
            </div>
          )}
        </div>
      )}

      {/* Discover Teams Panel */}
      {activeTab === 'discover' && (
        <div id="discover-panel" role="tabpanel">
          {/* Loading State */}
          {discoverLoading && (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500 dark:text-gray-400">Searching teams...</p>
            </div>
          )}

          {/* Empty State */}
          {!discoverLoading && discoverTeams.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Compass className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" aria-hidden="true" />
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {searchQuery
                  ? `No teams found matching "${searchQuery}".`
                  : 'No other teams available to join right now.'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => setShowCreateDialog(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  <Plus className="w-4 h-4" aria-hidden="true" />
                  Create a Team
                </button>
              )}
            </div>
          )}

          {/* Discover Grid */}
          {!discoverLoading && discoverTeams.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {discoverTeams.map((team) => renderTeamCard(team, false))}
              </div>

              {/* Pagination */}
              {discoverTotalCount > PAGE_SIZE && (
                <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Showing {discoverSkip + 1}-{Math.min(discoverSkip + PAGE_SIZE, discoverTotalCount)} of {discoverTotalCount}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={isFirstPage}
                      aria-label="Previous page"
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Prev
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={isLastPage}
                      aria-label="Next page"
                      className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Create Team Dialog */}
      <CreateTeamDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      />
    </div>
  );
};

export default MyTeamsPage;
